import Constants from '@/Constants';
import { QuizResult } from '@/models/QuizResult';
import { ApplicationState } from '@/store';
import { LocaleData } from '@/translations/LocaleData';
import { mdiScaleBalance } from '@mdi/js';
import Icon from '@mdi/react';
import React, { useEffect, useRef } from 'react';
import Modal from 'react-bootstrap/esm/Modal';
import { useSelector } from 'react-redux';
import { ModalMode } from './types';

export interface ModalProps {
    mode: ModalMode;
    resultsWithNoQuestions?: QuizResult[];
    setModalMode?: (mode: ModalMode) => void;
    onClose?: () => void;
}

type ModalContentProps = ModalProps & { strings: LocaleData };

export const modalHintStyle: React.CSSProperties = {
    top: 'calc(50% - (306px * 0.5))',
    height: '306px',
};

export const modalWhyStyle: React.CSSProperties = {
    top: 'calc(50% - (523px * 0.5))',
    height: '523px',
};

export const modalStyles: Record<ModalMode, React.CSSProperties> = {
    [ModalMode.CLOSED]: modalHintStyle,
    [ModalMode.HINT]: modalHintStyle,
    [ModalMode.WHY]: modalWhyStyle,
    [ModalMode.RESULTS_WITHOUT_QUESTION]: modalHintStyle,
};

export const ModalContentHint: React.FC<ModalContentProps> = ({ strings, setModalMode }: ModalContentProps) => (
    <Modal.Body>
        <div className="modal-icon">
            <Icon color={'#707070'} horizontal path={mdiScaleBalance} size={2} />
        </div>
        <h4>{strings.DISTRIBUTE_ANSWERS_PROPORTIONALLY}</h4>
        <p>{strings.EACH_RESULT}</p>
        <div className="create-quiz-question-modal-buttons">
            <button className="button outlined left" onClick={() => setModalMode && setModalMode(ModalMode.WHY)} type="button">
                {strings.WHY}
            </button>
            <button className="button filled right" onClick={() => setModalMode && setModalMode(ModalMode.CLOSED)} type="button">
                {strings.GOT_IT}
            </button>
        </div>
    </Modal.Body>
);

export const ModalContentWhy = ({ strings, setModalMode }: ModalContentProps) => {
    const domRef = useRef(null);

    useEffect(() => {
        if (!domRef.current) return;
        const div = document.createElement('div');
        div.innerHTML = strings.QUIZ_QUESTION_HINT;
        domRef.current && ((domRef.current as unknown) as HTMLDivElement).appendChild(div);
    }, [domRef, strings]);

    return (
        <Modal.Body>
            <div className="modal-icon">
                <Icon color={'#707070'} horizontal path={mdiScaleBalance} size={2} />
            </div>
            <h4>{strings.DISTRIBUTE_ANSWERS_PROPORTIONALLY}</h4>
            <p>{strings.EACH_RESULT}</p>
            <div className="create-quiz-question-modal-disclaimer" ref={domRef}></div>
            <div className="create-quiz-question-modal-buttons">
                <button className="button filled" onClick={() => setModalMode && setModalMode(ModalMode.CLOSED)} type="button">
                    {strings.GOT_IT}
                </button>
            </div>
        </Modal.Body>
    );
};

export const ModalContentResultsWithoutQuestion = ({ strings, onClose, resultsWithNoQuestions }: ModalContentProps) => {
    const domRef = useRef(null);

    useEffect(() => {
        if (!domRef.current) return;
        const div = document.createElement('div');
        div.innerHTML = strings.ADD_MORE_QUESTIONS_FOR_RESULTS;
        domRef.current && ((domRef.current as unknown) as HTMLDivElement).appendChild(div);
    }, [domRef, strings]);

    return (
        <Modal.Body>
            <div>
                <i className="material-icons-outlined align-middle error-icon">{Constants.icons.REPORT_PROBLEM}</i>
            </div>
            <h4>{strings.ATTENTION}</h4>
            <p>{strings.RESULTS_WITHOUT_QUESTION}</p>
            {resultsWithNoQuestions?.map((res: QuizResult, i: number) => (
                <p className="results-without-question-warning" key={i}>
                    {res.description}
                </p>
            ))}

            <div className="create-quiz-question-modal-disclaimer results-without-question" ref={domRef}></div>
            <div className="create-quiz-question-modal-buttons">
                <button className="button filled right" onClick={onClose} type="button">
                    {strings.UNDERSTAND}
                </button>
            </div>
        </Modal.Body>
    );
};

export const QuizModalBody: React.FC<ModalProps> = (props: ModalProps) => {
    const { strings } = useSelector((state: ApplicationState) => ({
        strings: state.locale.data,
    }));

    if (props.mode === ModalMode.HINT) {
        return <ModalContentHint strings={strings} {...props} />;
    }

    if (props.mode === ModalMode.WHY) {
        return <ModalContentWhy strings={strings} {...props} />;
    }

    if (props.mode === ModalMode.RESULTS_WITHOUT_QUESTION) {
        return <ModalContentResultsWithoutQuestion strings={strings} {...props} />;
    }

    return null;
};
